import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type FocusEvent as ReactFocusEvent,
} from 'react';
import {
  loadVolumePrefs,
  publishVolumeChange,
  subscribeVolumeChanges,
  type VolumePrefs,
} from '../game/audio/volumePrefs';

type VolumeKind = 'master' | 'music' | 'sfx';

const VOLUME_KINDS: readonly VolumeKind[] = ['master', 'music', 'sfx'] as const;

const VOLUME_LABELS: Record<VolumeKind, string> = {
  master: 'Master',
  music: 'Music',
  sfx: 'SFX',
};

const PANEL_ID = 'top-nav-volume-panel';

// Small delay before closing on mouseleave so the cursor can travel from the
// trigger across the gap to the panel (or back) without losing the open state.
const CLOSE_DELAY_MS = 150;

/**
 * Speaker glyph reflecting current master volume. Crosses out when silenced
 * (master = 0) and grows extra waves at higher volumes. Pure SVG so it
 * inherits `color` from CSS and stays crisp at any zoom level.
 */
function SpeakerIcon({ master }: { master: number }) {
  const silenced = master <= 0;
  return (
    <svg
      className={`top-nav-volume-icon ${silenced ? 'is-silenced' : ''}`}
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 10v4h3.5L12 17.5V6.5L7.5 10H4z" fill="currentColor" />
      {!silenced && master > 0.55 && (
        <path
          d="M18.5 8.5c1.4 1.4 1.4 5.6 0 7"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          fill="none"
        />
      )}
      {!silenced && (
        <path
          d="M15.5 10.5c0.9 0.9 0.9 2.1 0 3"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          fill="none"
        />
      )}
      {silenced && (
        <path
          d="M16 9l5 6m0-6l-5 6"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          fill="none"
        />
      )}
    </svg>
  );
}

/**
 * Always-visible speaker icon in the global top nav. Hovering, focusing, or
 * clicking the icon reveals a popover with three sliders — master / music /
 * SFX — the only place in the app where volume is changed. Persistence and
 * cross-tab sync flow through `publishVolumeChange` / `subscribeVolumeChanges`
 * in volumePrefs.ts so the active game session updates audibly in the same
 * frame the slider moves.
 *
 * The disclosure pattern (button with `aria-expanded` + `aria-controls`)
 * keeps the control reachable by mouse, touch, and keyboard. Hover opens
 * after a short delay-cancel; click toggles a sticky open state for touch
 * users; Escape closes and returns focus to the trigger.
 */
export function TopNavVolume() {
  const [volumes, setVolumes] = useState<VolumePrefs>(() => loadVolumePrefs());
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return subscribeVolumeChanges(setVolumes);
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const open = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsOpen(true);
  }, []);

  const scheduleClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
    }, CLOSE_DELAY_MS);
  }, []);

  const closeNow = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsOpen(false);
  }, []);

  // Reading from localStorage rather than React state means rapid slider
  // drags don't lose intermediate field changes to closure staleness —
  // each onChange composes against the latest persisted value, which is
  // written synchronously inside `publishVolumeChange`.
  const onVolumeChange = useCallback((kind: VolumeKind, value: number) => {
    publishVolumeChange({ ...loadVolumePrefs(), [kind]: value });
  }, []);

  const onTriggerClick = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape' && isOpen) {
        e.stopPropagation();
        closeNow();
        triggerRef.current?.focus();
      }
    },
    [closeNow, isOpen],
  );

  const onBlurCapture = useCallback(
    (e: ReactFocusEvent<HTMLDivElement>) => {
      if (!wrapperRef.current?.contains(e.relatedTarget as Node | null)) {
        closeNow();
      }
    },
    [closeNow],
  );

  const masterPercent = Math.round(volumes.master * 100);

  return (
    <div
      ref={wrapperRef}
      className="top-nav-volume"
      data-testid="top-nav-volume"
      data-open={isOpen ? 'true' : undefined}
      onMouseEnter={open}
      onMouseLeave={scheduleClose}
      onFocus={open}
      onBlur={onBlurCapture}
      onKeyDown={onKeyDown}
    >
      <button
        ref={triggerRef}
        type="button"
        className="top-nav-volume-trigger"
        aria-label="Volume settings"
        aria-expanded={isOpen}
        aria-controls={PANEL_ID}
        onClick={onTriggerClick}
        title={`Master ${masterPercent}%`}
        data-testid="top-nav-volume-trigger"
      >
        <SpeakerIcon master={volumes.master} />
      </button>
      <div
        id={PANEL_ID}
        className="top-nav-volume-panel"
        role="group"
        aria-label="Volume controls"
        data-testid="top-nav-volume-panel"
      >
        {VOLUME_KINDS.map((kind) => {
          const percent = Math.round(volumes[kind] * 100);
          return (
            <div className="top-nav-volume-row" key={kind}>
              <label htmlFor={`top-nav-vol-${kind}`}>{VOLUME_LABELS[kind]}</label>
              <input
                id={`top-nav-vol-${kind}`}
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={volumes[kind]}
                onChange={(e) => onVolumeChange(kind, Number(e.target.value))}
                aria-valuetext={`${percent} percent`}
                data-testid={`top-nav-volume-${kind}`}
              />
              <span
                className="top-nav-volume-readout"
                data-testid={`top-nav-volume-${kind}-readout`}
              >
                {percent}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
